/**
 * Geospatial helpers.
 *
 * WHY GEOHASH (gap D6)
 * Firestore cannot answer "everything within 5 km of here" natively. Storing a
 * geohash prefix on every wire, case and public report turns a radius query into
 * a set of range queries on a string field, which Firestore does support. This
 * one field unlocks three separate features:
 *   - map viewport queries that do not read the entire collection (which is also
 *     what keeps us inside the Firestore free read quota — see PART F);
 *   - clustering several citizen reports of the same spot into one incident;
 *   - the repeat-offender Mogha heatmap.
 *
 * The geohash implementation is inlined rather than pulled from geofire-common
 * because it is ~40 lines, it must run identically in the browser, in a
 * Cloudflare Worker and in Node, and it is the kind of primitive that should not
 * silently change under a dependency bump.
 */
/* -------------------------------------------------------------------------- */
/* Punjab bounds                                                              */
/* -------------------------------------------------------------------------- */
/**
 * Approximate bounding box of Punjab province, Pakistan, with a small margin.
 *
 * Used to reject obviously-bogus public submissions at intake before they reach
 * Firestore. It is deliberately a coarse box, not a precise boundary polygon:
 * a citizen standing near a district edge must never be turned away, so the
 * check errs generously. Rejecting a genuine report is a worse failure than
 * accepting a stray one, which an officer will close as CLOSED_NO_THEFT anyway.
 */
export const PUNJAB_BOUNDS = {
    minLat: 27.5,
    maxLat: 34.2,
    minLng: 69.1,
    maxLng: 75.6,
};
export function isValidLatLng(p) {
    return (Number.isFinite(p.lat) &&
        Number.isFinite(p.lng) &&
        p.lat >= -90 &&
        p.lat <= 90 &&
        p.lng >= -180 &&
        p.lng <= 180);
}
export function isWithinPunjab(p) {
    if (!isValidLatLng(p))
        return false;
    return (p.lat >= PUNJAB_BOUNDS.minLat &&
        p.lat <= PUNJAB_BOUNDS.maxLat &&
        p.lng >= PUNJAB_BOUNDS.minLng &&
        p.lng <= PUNJAB_BOUNDS.maxLng);
}
/* -------------------------------------------------------------------------- */
/* Geohash                                                                    */
/* -------------------------------------------------------------------------- */
const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';
/**
 * Encode a coordinate as a geohash.
 *
 * Precision 9 ≈ 4.8 m × 4.8 m, which is finer than consumer GPS accuracy and
 * therefore the right default: it preserves all the precision the device
 * actually has, and no more.
 */
export function geohashEncode(p, precision = 9) {
    if (!isValidLatLng(p))
        throw new Error('Invalid coordinate');
    if (precision < 1 || precision > 12)
        throw new Error('Precision must be 1..12');
    let latMin = -90;
    let latMax = 90;
    let lngMin = -180;
    let lngMax = 180;
    let hash = '';
    let bits = 0;
    let bitCount = 0;
    let even = true; // longitude first
    while (hash.length < precision) {
        if (even) {
            const mid = (lngMin + lngMax) / 2;
            if (p.lng >= mid) {
                bits = (bits << 1) | 1;
                lngMin = mid;
            }
            else {
                bits = bits << 1;
                lngMax = mid;
            }
        }
        else {
            const mid = (latMin + latMax) / 2;
            if (p.lat >= mid) {
                bits = (bits << 1) | 1;
                latMin = mid;
            }
            else {
                bits = bits << 1;
                latMax = mid;
            }
        }
        even = !even;
        bitCount += 1;
        if (bitCount === 5) {
            hash += BASE32[bits];
            bits = 0;
            bitCount = 0;
        }
    }
    return hash;
}
/** Approximate cell size in metres for a given geohash precision. */
export const GEOHASH_PRECISION_METRES = {
    1: 5_000_000,
    2: 1_250_000,
    3: 156_000,
    4: 39_100,
    5: 4_890,
    6: 1_220,
    7: 153,
    8: 38,
    9: 4.8,
};
/**
 * Shortest geohash prefix length whose cell comfortably contains `radiusMetres`.
 * Used to pick a coarse bucket for proximity/clustering queries.
 */
export function geohashPrecisionForRadius(radiusMetres) {
    for (let p = 9; p >= 1; p -= 1) {
        if ((GEOHASH_PRECISION_METRES[p] ?? 0) >= radiusMetres)
            return p;
    }
    return 1;
}
/* -------------------------------------------------------------------------- */
/* Distance                                                                   */
/* -------------------------------------------------------------------------- */
const EARTH_RADIUS_M = 6_371_008.8;
const toRad = (deg) => (deg * Math.PI) / 180;
/** Great-circle distance in metres. */
export function haversineMetres(a, b) {
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
/* -------------------------------------------------------------------------- */
/* Public-report clustering (gap D6)                                          */
/* -------------------------------------------------------------------------- */
/**
 * Default radius within which two citizen reports are treated as the same
 * incident. 250 m is roughly the length of canal an onlooker would describe as
 * "the same place", and is wide enough to absorb phone GPS drift.
 */
export const REPORT_CLUSTER_RADIUS_M = 250;
/**
 * Cluster key for deduplicating public reports. Reports sharing a key are
 * CANDIDATES for merging — an officer confirms, the system does not merge
 * silently. Two genuine thefts 200 m apart on the same distributary are
 * entirely possible, and auto-merging them would hide one.
 */
export function reportClusterKey(p) {
    return geohashEncode(p, geohashPrecisionForRadius(REPORT_CLUSTER_RADIUS_M));
}
/* -------------------------------------------------------------------------- */
/* EXIF cross-check (gap D2)                                                  */
/* -------------------------------------------------------------------------- */
/**
 * Tolerance between a photo's embedded EXIF GPS and the coordinates the officer
 * claims. Beyond this the evidence is FLAGGED, never rejected — many field
 * phones and most messaging apps strip EXIF entirely, so absence of EXIF is
 * normal and must not block a genuine report. The flag is what a hearing needs:
 * visible, recorded, and for the adjudicating officer to weigh.
 */
export const EXIF_LOCATION_TOLERANCE_M = 500;
export function verifyExifLocation(claimed, exif) {
    if (!exif || !isValidLatLng(exif))
        return { status: 'no_exif', distanceMetres: null };
    const distanceMetres = haversineMetres(claimed, exif);
    return {
        status: distanceMetres <= EXIF_LOCATION_TOLERANCE_M ? 'verified' : 'mismatch',
        distanceMetres,
    };
}
//# sourceMappingURL=geo.js.map